import { useState, useEffect, useCallback } from 'react'
import {
  Stack,
  Text,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
  Spinner,
  SpinnerSize,
  Separator,
  Icon,
  ChoiceGroup,
  IChoiceGroupOption,
  Toggle,
  SpinButton,
  TextField,
  Dialog,
  DialogType,
  DialogFooter,
} from '@fluentui/react'
import { useMsal } from '@azure/msal-react'
import { apiService } from '../services/apiService'
import { useTheme } from '../context/ThemeContext'
import {
  useDashboardPrefs,
  CHART_TYPE_OPTIONS,
  DashboardChartType,
} from '../context/DashboardPrefsContext'

interface SyncStats {
  total_fetched: number
  new_users: number
  updated_users: number
  reactivated_users?: number
  departed_users?: number
  promoted_owners?: number
  applications_affected?: number
  duration_seconds?: number
  errors: number
}

interface AppSyncResult {
  success: boolean
  totalApplications: number
  newApplications: number
  updatedApplications: number
  deletedApplications: number
  totalCredentials: number
  deletedCredentials: number
  durationSeconds: number
  errorMessage?: string
  errors?: string[]
}


interface AppSetting {
  settingId: number
  category: string
  key: string
  value: string
  dataType: string
  description?: string
}

const riskModeOptions: IChoiceGroupOption[] = [
  {
    key: 'Manual',
    text: 'Manual - Admins manually set risk levels for each application',
  },
  {
    key: 'Automatic',
    text: 'Automatic - System calculates risk based on credentials and owners',
  },
  {
    key: 'Hybrid',
    text: 'Hybrid (Recommended) - System suggests, admins can override',
  },
]

const themeModeOptions: IChoiceGroupOption[] = [
  {
    key: 'light',
    text: 'Light - Classic light theme',
  },
  {
    key: 'dark',
    text: 'Dark - Easy on the eyes in low light',
  },
]

const chartTypeChoiceOptions: IChoiceGroupOption[] = CHART_TYPE_OPTIONS.map(o => ({
  key: o.key,
  text: `${o.label} — ${o.description}`,
}))

export function Settings() {
  const { accounts } = useMsal()
  const { themeMode, setThemeMode } = useTheme()
  const { chartType, setChartType, showValues, setShowValues } = useDashboardPrefs()
  const [isSyncing, setIsSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState<{ success: boolean; message: string; stats?: SyncStats } | null>(null)
  const [riskMode, setRiskMode] = useState<string>('Hybrid')
  const [isLoadingSettings, setIsLoadingSettings] = useState(true)
  const [isSavingRiskMode, setIsSavingRiskMode] = useState(false)
  const [riskModeMessage, setRiskModeMessage] = useState<{ success: boolean; message: string } | null>(null)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [recalculateResult, setRecalculateResult] = useState<{ success: boolean; message: string } | null>(null)
  const [isSyncingApps, setIsSyncingApps] = useState(false)
  const [appSyncResult, setAppSyncResult] = useState<AppSyncResult | null>(null)

  // Security settings state
  const [rateLimitEnabled, setRateLimitEnabled] = useState(true)
  const [rateLimitRpm, setRateLimitRpm] = useState(60)
  const [corsAllowedOrigins, setCorsAllowedOrigins] = useState('')
  const [corsAllowAll, setCorsAllowAll] = useState(false)
  const [isLoadingSecuritySettings, setIsLoadingSecuritySettings] = useState(true)
  const [isSavingSecuritySetting, setIsSavingSecuritySetting] = useState(false)
  const [securitySettingMessage, setSecuritySettingMessage] = useState<{ success: boolean; message: string } | null>(null)
  const [showCorsWarningDialog, setShowCorsWarningDialog] = useState(false)

  // Synthetic data state
  const [syntheticEnabled, setSyntheticEnabled] = useState(false)
  const [syntheticMaxApps, setSyntheticMaxApps] = useState(50)
  const [isSavingSynthetic, setIsSavingSynthetic] = useState(false)
  const [syntheticMessage, setSyntheticMessage] = useState<{ success: boolean; message: string } | null>(null)
  const [isRunningSynthetic, setIsRunningSynthetic] = useState(false)
  const [isCleaningSynthetic, setIsCleaningSynthetic] = useState(false)
  const [showSyntheticCleanupDialog, setShowSyntheticCleanupDialog] = useState(false)

  const account = accounts[0]
  const userName = account?.name || 'User'
  const userEmail = account?.username || ''

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await apiService.getSettings('RiskManagement')
        const modeSetting = settings.find((s: AppSetting) => s.key === 'CalculationMode')
        if (modeSetting) {
          setRiskMode(modeSetting.value)
        }
      } catch (err) {
        console.error('Failed to load settings:', err)
      } finally {
        setIsLoadingSettings(false)
      }
    }
    loadSettings()
  }, [])

  // Load synthetic data settings on mount
  useEffect(() => {
    const loadSyntheticSettings = async () => {
      try {
        const settings = await apiService.getSettings('SyntheticData')
        for (const setting of settings) {
          if (setting.key === 'Enabled') {
            setSyntheticEnabled(setting.value.toLowerCase() === 'true')
          } else if (setting.key === 'MaxApps') {
            setSyntheticMaxApps(parseInt(setting.value, 10) || 50)
          }
        }
      } catch (err) {
        // Setting category may not exist yet (migration not run); silently default.
        console.debug('SyntheticData settings not found:', err)
      }
    }
    loadSyntheticSettings()
  }, [])

  // Load security settings on mount
  useEffect(() => {
    const loadSecuritySettings = async () => {
      try {
        const settings = await apiService.getSettings('Security')
        for (const setting of settings) {
          switch (setting.key) {
            case 'RateLimitEnabled':
              setRateLimitEnabled(setting.value.toLowerCase() === 'true')
              break
            case 'RateLimitRequestsPerMinute':
              setRateLimitRpm(parseInt(setting.value, 10) || 60)
              break
            case 'CorsAllowedOrigins':
              try {
                const origins = JSON.parse(setting.value)
                setCorsAllowedOrigins(Array.isArray(origins) ? origins.join('\n') : '')
              } catch {
                setCorsAllowedOrigins('')
              }
              break
            case 'CorsAllowAllOrigins':
              setCorsAllowAll(setting.value.toLowerCase() === 'true')
              break
          }
        }
      } catch (err) {
        console.error('Failed to load security settings:', err)
      } finally {
        setIsLoadingSecuritySettings(false)
      }
    }
    loadSecuritySettings()
  }, [])

  const handleRiskModeChange = async (newMode: string) => {
    setIsSavingRiskMode(true)
    setRiskModeMessage(null)
    try {
      await apiService.updateSetting('RiskManagement', 'CalculationMode', newMode)
      setRiskMode(newMode)
      setRiskModeMessage({ success: true, message: 'Risk calculation mode updated successfully' })
    } catch (err) {
      setRiskModeMessage({
        success: false,
        message: `Failed to update: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsSavingRiskMode(false)
    }
  }

  const handleRecalculateRisk = async () => {
    setIsRecalculating(true)
    setRecalculateResult(null)
    try {
      const result = await apiService.recalculateRiskLevels()
      setRecalculateResult({
        success: true,
        message: `Risk levels recalculated for ${result.applicationsUpdated} applications`,
      })
    } catch (err) {
      setRecalculateResult({
        success: false,
        message: `Failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsRecalculating(false)
    }
  }

  const handleSyncUsers = async () => {
    setIsSyncing(true)
    setSyncResult(null)
    try {
      const result = await apiService.syncUsersFromAzureAD()
      setSyncResult({
        success: true,
        message: 'User sync completed successfully',
        stats: result.stats,
      })
    } catch (err) {
      setSyncResult({
        success: false,
        message: `Sync failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsSyncing(false)
    }
  }

  const handleSyncApplications = async () => {
    setIsSyncingApps(true)
    setAppSyncResult(null)
    try {
      const result = await apiService.syncApplications()
      setAppSyncResult(result)
    } catch (err) {
      setAppSyncResult({
        success: false,
        totalApplications: 0,
        newApplications: 0,
        updatedApplications: 0,
        deletedApplications: 0,
        totalCredentials: 0,
        deletedCredentials: 0,
        durationSeconds: 0,
        errorMessage: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setIsSyncingApps(false)
    }
  }

  const saveSyntheticSetting = useCallback(async (key: string, value: string) => {
    setIsSavingSynthetic(true)
    setSyntheticMessage(null)
    try {
      await apiService.updateSetting('SyntheticData', key, value)
      setSyntheticMessage({ success: true, message: `${key} updated` })
    } catch (err) {
      setSyntheticMessage({
        success: false,
        message: `Failed to update ${key}: ${err instanceof Error ? err.message : 'Unknown error'}. ` +
          `If this is the first time, run database migration 007_synthetic_data_settings.sql.`,
      })
    } finally {
      setIsSavingSynthetic(false)
    }
  }, [])

  const handleSyntheticToggle = async (checked: boolean) => {
    setSyntheticEnabled(checked)
    await saveSyntheticSetting('Enabled', checked.toString())
  }

  const handleSyntheticMaxAppsChange = async (_: unknown, newValue?: string) => {
    if (newValue) {
      const n = parseInt(newValue, 10)
      if (n >= 5 && n <= 500) {
        setSyntheticMaxApps(n)
        await saveSyntheticSetting('MaxApps', n.toString())
      }
    }
  }

  const handleRunSyntheticNow = async () => {
    setIsRunningSynthetic(true)
    setSyntheticMessage(null)
    try {
      const result = await apiService.runSyntheticDataNow()
      setSyntheticMessage({
        success: true,
        message: `Run complete: action=${result.action}, added=${result.added}, modified=${result.modified}, removed=${result.removed}.`,
      })
    } catch (err) {
      setSyntheticMessage({
        success: false,
        message: `Run failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsRunningSynthetic(false)
    }
  }

  const handleSyntheticCleanup = async () => {
    setShowSyntheticCleanupDialog(false)
    setIsCleaningSynthetic(true)
    setSyntheticMessage(null)
    try {
      const result = await apiService.cleanupSyntheticData()
      setSyntheticMessage({
        success: true,
        message: `Cleanup complete: deleted ${result.deleted} of ${result.total_found} synthetic apps. ${result.errors > 0 ? `(${result.errors} errors)` : ''}`,
      })
    } catch (err) {
      setSyntheticMessage({
        success: false,
        message: `Cleanup failed: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsCleaningSynthetic(false)
    }
  }

  const saveSecuritySetting = useCallback(async (key: string, value: string) => {
    setIsSavingSecuritySetting(true)
    setSecuritySettingMessage(null)
    try {
      await apiService.updateSetting('Security', key, value)
      setSecuritySettingMessage({ success: true, message: `${key} updated successfully` })
    } catch (err) {
      setSecuritySettingMessage({
        success: false,
        message: `Failed to update ${key}: ${err instanceof Error ? err.message : 'Unknown error'}`,
      })
    } finally {
      setIsSavingSecuritySetting(false)
    }
  }, [])

  const handleRateLimitToggle = async (checked: boolean) => {
    setRateLimitEnabled(checked)
    await saveSecuritySetting('RateLimitEnabled', checked.toString())
  }

  const handleRateLimitRpmChange = async (_: unknown, newValue?: string) => {
    if (newValue) {
      const rpm = parseInt(newValue, 10)
      if (rpm >= 10 && rpm <= 1000) {
        setRateLimitRpm(rpm)
        await saveSecuritySetting('RateLimitRequestsPerMinute', rpm.toString())
      }
    }
  }

  const handleCorsOriginsBlur = async () => {
    const origins = corsAllowedOrigins
      .split('\n')
      .map(o => o.trim())
      .filter(o => o.length > 0)
    await saveSecuritySetting('CorsAllowedOrigins', JSON.stringify(origins))
  }

  const handleCorsAllowAllToggle = (checked: boolean) => {
    if (checked) {
      setShowCorsWarningDialog(true)
    } else {
      setCorsAllowAll(false)
      saveSecuritySetting('CorsAllowAllOrigins', 'false')
    }
  }

  const confirmCorsAllowAll = async () => {
    setCorsAllowAll(true)
    setShowCorsWarningDialog(false)
    await saveSecuritySetting('CorsAllowAllOrigins', 'true')
  }


  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-description">
          Configure application settings and manage data synchronization
        </p>
      </div>

      {/* User Info Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 16, display: 'block' } }}>
          Current User
        </Text>
        <Stack tokens={{ childrenGap: 8 }}>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Icon iconName="Contact" styles={{ root: { fontSize: 16, color: 'var(--primary-color)' } }} />
            <Text>{userName}</Text>
          </Stack>
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            <Icon iconName="Mail" styles={{ root: { fontSize: 16, color: 'var(--primary-color)' } }} />
            <Text>{userEmail}</Text>
          </Stack>
        </Stack>
      </div>

      {/* Appearance Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 8, display: 'block' } }}>
          Appearance
        </Text>
        <Text styles={{ root: { color: 'var(--text-secondary)', marginBottom: 16, display: 'block' } }}>
          Customize the look and feel of the admin portal.
        </Text>

        <Separator />

        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 16 } }}>
          <Text styles={{ root: { fontWeight: 600 } }}>Theme</Text>
          <ChoiceGroup
            selectedKey={themeMode}
            options={themeModeOptions}
            onChange={(_, option) => option && setThemeMode(option.key as 'light' | 'dark')}
          />
        </Stack>

        <Separator styles={{ root: { marginTop: 24 } }} />

        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 16 } }}>
          <Stack tokens={{ childrenGap: 2 }}>
            <Text styles={{ root: { fontWeight: 600 } }}>Dashboard chart type</Text>
            <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 13 } }}>
              How the Lifecycle Command Center plots daily change activity.
            </Text>
          </Stack>
          <ChoiceGroup
            selectedKey={chartType}
            options={chartTypeChoiceOptions}
            onChange={(_, option) =>
              option && setChartType(option.key as DashboardChartType)
            }
          />

          <Toggle
            label="Show values on chart"
            checked={showValues}
            onText="On"
            offText="Off"
            onChange={(_, checked) => setShowValues(!!checked)}
            styles={{ root: { marginBottom: 0 } }}
          />
          <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 13, marginTop: -8 } }}>
            Overlays per-category counts across whichever chart type is selected.
          </Text>
        </Stack>
      </div>

      {/* Risk Management Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 8, display: 'block' } }}>
          Risk Management
        </Text>
        <Text styles={{ root: { color: 'var(--text-secondary)', marginBottom: 16, display: 'block' } }}>
          Configure how application risk levels are determined.
        </Text>

        <Separator />

        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 16 } }}>
          <Text styles={{ root: { fontWeight: 600 } }}>Risk Calculation Mode</Text>

          {isLoadingSettings ? (
            <Spinner size={SpinnerSize.small} label="Loading settings..." />
          ) : (
            <ChoiceGroup
              selectedKey={riskMode}
              options={riskModeOptions}
              onChange={(_, option) => option && handleRiskModeChange(option.key)}
              disabled={isSavingRiskMode}
              styles={{ root: { marginBottom: 16 } }}
            />
          )}

          {isSavingRiskMode && (
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Spinner size={SpinnerSize.small} />
              <Text styles={{ root: { color: 'var(--text-secondary)' } }}>Saving...</Text>
            </Stack>
          )}

          {riskModeMessage && (
            <MessageBar
              messageBarType={riskModeMessage.success ? MessageBarType.success : MessageBarType.error}
              onDismiss={() => setRiskModeMessage(null)}
            >
              {riskModeMessage.message}
            </MessageBar>
          )}

          <Separator />

          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack tokens={{ childrenGap: 4 }}>
              <Text styles={{ root: { fontWeight: 600 } }}>Recalculate All Risk Levels</Text>
              <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 12 } }}>
                Trigger a recalculation of risk levels for all applications based on current rules.
              </Text>
            </Stack>
            <DefaultButton
              iconProps={{ iconName: 'Calculator' }}
              text={isRecalculating ? 'Calculating...' : 'Recalculate'}
              onClick={handleRecalculateRisk}
              disabled={isRecalculating || riskMode === 'Manual'}
              title={riskMode === 'Manual' ? 'Risk recalculation is disabled in Manual mode' : undefined}
              styles={{ root: { minWidth: 140 } }}
            />
          </Stack>

          {isRecalculating && (
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Spinner size={SpinnerSize.small} />
              <Text styles={{ root: { color: 'var(--text-secondary)' } }}>Recalculating risk levels...</Text>
            </Stack>
          )}

          {recalculateResult && (
            <MessageBar
              messageBarType={recalculateResult.success ? MessageBarType.success : MessageBarType.error}
              onDismiss={() => setRecalculateResult(null)}
            >
              {recalculateResult.message}
            </MessageBar>
          )}
        </Stack>
      </div>

      {/* Data Synchronization Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 8, display: 'block' } }}>
          Data Synchronization
        </Text>
        <Text styles={{ root: { color: 'var(--text-secondary)', marginBottom: 16, display: 'block' } }}>
          Sync data from Azure Active Directory to keep owners and application information up to date.
        </Text>

        <Separator />

        {/* Sync Users Section */}
        <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 16 } }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack tokens={{ childrenGap: 4 }}>
              <Text styles={{ root: { fontWeight: 600 } }}>Sync Users from Azure AD</Text>
              <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 12 } }}>
                Full sync of users from Azure AD. Adds new users, updates existing users, and marks departed users
                (disabled/deleted in Entra ID). Departed users are removed from application ownership and secondary
                owners are promoted. Runs daily at 3:00 AM UTC.
              </Text>
            </Stack>
            <PrimaryButton
              iconProps={{ iconName: 'Sync' }}
              text={isSyncing ? 'Syncing...' : 'Sync Users'}
              onClick={handleSyncUsers}
              disabled={isSyncing}
              styles={{ root: { minWidth: 140 } }}
            />
          </Stack>

          {isSyncing && (
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Spinner size={SpinnerSize.small} />
              <Text styles={{ root: { color: 'var(--text-secondary)' } }}>
                Syncing users from Azure AD... This may take a few minutes for large directories.
              </Text>
            </Stack>
          )}

          {syncResult && (
            <MessageBar
              messageBarType={syncResult.success ? MessageBarType.success : MessageBarType.error}
              onDismiss={() => setSyncResult(null)}
              isMultiline
            >
              <Stack tokens={{ childrenGap: 8 }}>
                <Text styles={{ root: { fontWeight: 600 } }}>
                  {syncResult.success
                    ? `User sync completed in ${syncResult.stats?.duration_seconds?.toFixed(1) || '?'}s`
                    : syncResult.message}
                </Text>
                {syncResult.stats && (
                  <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                    <Text>Total fetched: {syncResult.stats.total_fetched}</Text>
                    <Text styles={{ root: { color: 'var(--success-color)' } }}>
                      New: {syncResult.stats.new_users}
                    </Text>
                    <Text>Updated: {syncResult.stats.updated_users}</Text>
                    {(syncResult.stats.reactivated_users ?? 0) > 0 && (
                      <Text styles={{ root: { color: 'var(--success-color)' } }}>
                        Reactivated: {syncResult.stats.reactivated_users}
                      </Text>
                    )}
                    {(syncResult.stats.departed_users ?? 0) > 0 && (
                      <Text styles={{ root: { color: 'var(--error-color)' } }}>
                        Departed: {syncResult.stats.departed_users}
                      </Text>
                    )}
                    {(syncResult.stats.promoted_owners ?? 0) > 0 && (
                      <Text styles={{ root: { color: 'var(--warning-color)' } }}>
                        Promoted: {syncResult.stats.promoted_owners}
                      </Text>
                    )}
                    {(syncResult.stats.applications_affected ?? 0) > 0 && (
                      <Text styles={{ root: { color: 'var(--warning-color)' } }}>
                        Apps affected: {syncResult.stats.applications_affected}
                      </Text>
                    )}
                    {syncResult.stats.errors > 0 && (
                      <Text styles={{ root: { color: 'var(--error-color)' } }}>
                        Errors: {syncResult.stats.errors}
                      </Text>
                    )}
                  </Stack>
                )}
              </Stack>
            </MessageBar>
          )}
        </Stack>

        <Separator styles={{ root: { marginTop: 24, marginBottom: 24 } }} />

        {/* Sync Applications Section */}
        <Stack tokens={{ childrenGap: 16 }}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Stack tokens={{ childrenGap: 4 }}>
              <Text styles={{ root: { fontWeight: 600 } }}>Sync Applications</Text>
              <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 12 } }}>
                Sync SSO applications and credentials from Azure AD. This runs automatically daily at 2:00 AM UTC, or you can trigger it manually.
              </Text>
            </Stack>
            <PrimaryButton
              iconProps={{ iconName: 'CloudDownload' }}
              text={isSyncingApps ? 'Syncing...' : 'Run Now'}
              onClick={handleSyncApplications}
              disabled={isSyncingApps}
              styles={{ root: { minWidth: 140 } }}
            />
          </Stack>

          {isSyncingApps && (
            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
              <Spinner size={SpinnerSize.small} />
              <Text styles={{ root: { color: 'var(--text-secondary)' } }}>
                Syncing applications from Azure AD... This may take a few minutes.
              </Text>
            </Stack>
          )}

          {appSyncResult && (
            <MessageBar
              messageBarType={appSyncResult.success ? MessageBarType.success : MessageBarType.error}
              onDismiss={() => setAppSyncResult(null)}
              isMultiline
            >
              <Stack tokens={{ childrenGap: 8 }}>
                <Text styles={{ root: { fontWeight: 600 } }}>
                  {appSyncResult.success
                    ? `Application sync completed in ${appSyncResult.durationSeconds.toFixed(1)}s`
                    : `Sync failed: ${appSyncResult.errorMessage}`}
                </Text>
                {appSyncResult.success && (
                  <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                    <Text>Total: {appSyncResult.totalApplications}</Text>
                    <Text styles={{ root: { color: 'var(--success-color)' } }}>New: {appSyncResult.newApplications}</Text>
                    <Text>Updated: {appSyncResult.updatedApplications}</Text>
                    {appSyncResult.deletedApplications > 0 && (
                      <Text styles={{ root: { color: 'var(--error-color)' } }}>Deleted: {appSyncResult.deletedApplications}</Text>
                    )}
                    <Text>Credentials: {appSyncResult.totalCredentials}</Text>
                    {appSyncResult.deletedCredentials > 0 && (
                      <Text styles={{ root: { color: 'var(--error-color)' } }}>Deleted Creds: {appSyncResult.deletedCredentials}</Text>
                    )}
                  </Stack>
                )}
                {appSyncResult.errors && appSyncResult.errors.length > 0 && (
                  <Stack tokens={{ childrenGap: 4 }}>
                    <Text styles={{ root: { fontWeight: 600, color: 'var(--error-color)' } }}>Errors:</Text>
                    {appSyncResult.errors.slice(0, 5).map((error, i) => (
                      <Text key={i} styles={{ root: { fontSize: 12, color: 'var(--text-secondary)' } }}>
                        {error}
                      </Text>
                    ))}
                    {appSyncResult.errors.length > 5 && (
                      <Text styles={{ root: { fontSize: 12, color: 'var(--text-secondary)' } }}>
                        ...and {appSyncResult.errors.length - 5} more errors
                      </Text>
                    )}
                  </Stack>
                )}
              </Stack>
            </MessageBar>
          )}

        </Stack>
      </div>

      {/* Advanced Settings Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 8, display: 'block' } }}>
          Advanced Settings
        </Text>
        <Text styles={{ root: { color: 'var(--text-secondary)', marginBottom: 16, display: 'block' } }}>
          Configure security and API behavior settings.
        </Text>

        <MessageBar
          messageBarType={MessageBarType.warning}
          styles={{ root: { marginBottom: 16 } }}
        >
          <strong>Caution:</strong> These settings affect API security. Changes take effect immediately and may impact all users.
        </MessageBar>

        <Separator />

        {isLoadingSecuritySettings ? (
          <Stack tokens={{ childrenGap: 16 }} styles={{ root: { marginTop: 16 } }}>
            <Spinner size={SpinnerSize.small} label="Loading security settings..." />
          </Stack>
        ) : (
          <Stack tokens={{ childrenGap: 24 }} styles={{ root: { marginTop: 16 } }}>
            {/* Rate Limiting Section */}
            <Stack tokens={{ childrenGap: 12 }}>
              <Text styles={{ root: { fontWeight: 600, fontSize: 16 } }}>Rate Limiting</Text>
              <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 12 } }}>
                Limit the number of API requests per user to prevent abuse and ensure fair usage.
              </Text>

              <Stack horizontal tokens={{ childrenGap: 24 }} verticalAlign="center" wrap>
                <Toggle
                  label="Enable Rate Limiting"
                  checked={rateLimitEnabled}
                  onChange={(_, checked) => handleRateLimitToggle(checked ?? false)}
                  disabled={isSavingSecuritySetting}
                  inlineLabel
                />
                <SpinButton
                  label="Requests per minute"
                  min={10}
                  max={1000}
                  step={10}
                  value={rateLimitRpm.toString()}
                  onChange={handleRateLimitRpmChange}
                  disabled={!rateLimitEnabled || isSavingSecuritySetting}
                  styles={{ root: { width: 150 } }}
                />
              </Stack>
            </Stack>

            <Separator />

            {/* CORS Section */}
            <Stack tokens={{ childrenGap: 12 }}>
              <Text styles={{ root: { fontWeight: 600, fontSize: 16 } }}>CORS (Cross-Origin Resource Sharing)</Text>
              <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 12 } }}>
                Control which domains can make requests to the API. Default origins (admin portal and dashboard URLs) are always allowed.
              </Text>

              <TextField
                label="Additional Allowed Origins"
                placeholder="https://example.com&#10;https://app.example.com"
                multiline
                rows={4}
                value={corsAllowedOrigins}
                onChange={(_, value) => setCorsAllowedOrigins(value || '')}
                onBlur={handleCorsOriginsBlur}
                disabled={corsAllowAll || isSavingSecuritySetting}
                description="Enter one origin URL per line (e.g., https://example.com)"
              />

              <Stack tokens={{ childrenGap: 8 }}>
                <Toggle
                  label="Allow All Origins (DANGEROUS)"
                  checked={corsAllowAll}
                  onChange={(_, checked) => handleCorsAllowAllToggle(checked ?? false)}
                  disabled={isSavingSecuritySetting}
                  inlineLabel
                />
                {corsAllowAll && (
                  <MessageBar messageBarType={MessageBarType.severeWarning}>
                    <strong>Security Risk:</strong> Allowing all origins disables CORS protection. Any website can make API requests on behalf of authenticated users. Only enable this for development or testing.
                  </MessageBar>
                )}
              </Stack>
            </Stack>

            {isSavingSecuritySetting && (
              <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                <Spinner size={SpinnerSize.small} />
                <Text styles={{ root: { color: 'var(--text-secondary)' } }}>Saving...</Text>
              </Stack>
            )}

            {securitySettingMessage && (
              <MessageBar
                messageBarType={securitySettingMessage.success ? MessageBarType.success : MessageBarType.error}
                onDismiss={() => setSecuritySettingMessage(null)}
              >
                {securitySettingMessage.message}
              </MessageBar>
            )}
          </Stack>
        )}
      </div>

      {/* CORS Warning Dialog */}
      <Dialog
        hidden={!showCorsWarningDialog}
        onDismiss={() => setShowCorsWarningDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Security Warning',
          subText: 'Enabling "Allow All Origins" will disable CORS protection. This means ANY website can make authenticated API requests on behalf of your users. This is a significant security risk and should only be used for development or testing purposes.',
        }}
      >
        <DialogFooter>
          <DefaultButton onClick={() => setShowCorsWarningDialog(false)} text="Cancel" />
          <PrimaryButton onClick={confirmCorsAllowAll} text="I understand, enable it" styles={{ root: { backgroundColor: '#d13438' } }} />
        </DialogFooter>
      </Dialog>

      {/* Synthetic Data Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 8, display: 'block' } }}>
          Synthetic Data Generator
        </Text>
        <Text styles={{ root: { color: 'var(--text-secondary)', marginBottom: 16, display: 'block' } }}>
          Creates real Entra App Registrations tagged <code>synthetic-data</code> on a daily schedule (1:30 AM UTC) so the
          dashboard's change history has evolving data to show. Off by default. All synthetic apps are identifiable by the
          tag and can be wiped at any time with the Cleanup button.
        </Text>

        <MessageBar messageBarType={MessageBarType.warning} styles={{ root: { marginBottom: 16 } }}>
          <strong>Note:</strong> This creates real objects in your Entra tenant. The API service principal needs the{' '}
          <code>Application.ReadWrite.All</code> Graph permission. Real apps in your tenant are never touched — only apps
          with the <code>synthetic-data</code> tag are visible to this generator and to the cleanup endpoint.
        </MessageBar>

        <Stack tokens={{ childrenGap: 16 }}>
          <Stack horizontal tokens={{ childrenGap: 24 }} verticalAlign="center" wrap>
            <Toggle
              label="Enable daily generator"
              checked={syntheticEnabled}
              onChange={(_, checked) => handleSyntheticToggle(checked ?? false)}
              disabled={isSavingSynthetic}
              inlineLabel
            />
            <SpinButton
              label="Steady-state cap"
              min={5}
              max={500}
              step={5}
              value={syntheticMaxApps.toString()}
              onChange={handleSyntheticMaxAppsChange}
              disabled={isSavingSynthetic}
              styles={{ root: { width: 150 } }}
            />
          </Stack>

          <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
            <DefaultButton
              text={isRunningSynthetic ? 'Running...' : 'Run one cycle now'}
              iconProps={{ iconName: 'Play' }}
              onClick={handleRunSyntheticNow}
              disabled={isRunningSynthetic || isCleaningSynthetic}
            />
            <DefaultButton
              text={isCleaningSynthetic ? 'Cleaning...' : 'Cleanup all synthetic apps'}
              iconProps={{ iconName: 'Delete' }}
              onClick={() => setShowSyntheticCleanupDialog(true)}
              disabled={isRunningSynthetic || isCleaningSynthetic}
              styles={{ root: { color: '#a4262c' } }}
            />
          </Stack>

          {syntheticMessage && (
            <MessageBar
              messageBarType={syntheticMessage.success ? MessageBarType.success : MessageBarType.error}
              onDismiss={() => setSyntheticMessage(null)}
            >
              {syntheticMessage.message}
            </MessageBar>
          )}
        </Stack>
      </div>

      {/* Synthetic Cleanup Confirmation Dialog */}
      <Dialog
        hidden={!showSyntheticCleanupDialog}
        onDismiss={() => setShowSyntheticCleanupDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Delete all synthetic apps?',
          subText:
            'This will permanently delete every Entra App Registration tagged "synthetic-data". Real apps in your tenant are not affected. This action cannot be undone, but new synthetic apps will be created on the next daily run if the generator is enabled.',
        }}
      >
        <DialogFooter>
          <DefaultButton onClick={() => setShowSyntheticCleanupDialog(false)} text="Cancel" />
          <PrimaryButton
            onClick={handleSyntheticCleanup}
            text="Delete all synthetic apps"
            styles={{ root: { backgroundColor: '#a4262c' } }}
          />
        </DialogFooter>
      </Dialog>

      {/* About Card */}
      <div className="card">
        <Text variant="large" styles={{ root: { fontWeight: 600, marginBottom: 16, display: 'block' } }}>
          About
        </Text>
        <Stack tokens={{ childrenGap: 8 }}>
          <Text>SSO Reporting Dashboard</Text>
          <Text styles={{ root: { color: 'var(--text-secondary)', fontSize: 12 } }}>
            Version 1.0.0
          </Text>
        </Stack>
      </div>
    </div>
  )
}
